import React from 'react';
import OrderUpiQr from '@/components/OrderUpiQr';
import {
  customerPayUrls,
  openCustomerGPay,
  openCustomerPaytm,
  openCustomerPhonePe,
} from '@/lib/openUpiAppPay';

function formatInr(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('en-IN', { maximumFractionDigits: n % 1 ? 2 : 0 });
}

function posterUrlWithVpa(url, vpa) {
  const src = (url || '').trim();
  if (!src) return '';
  const separator = src.includes('?') ? '&' : '?';
  return `${src}${separator}vpa=${encodeURIComponent((vpa || '').trim())}`;
}

/** Primary payment block. TD scan-only mode uses the approved static business poster. */
function OrderUpiPrimaryBlock({ paymentDue, amountLabel, payee, isEarlyCod, scanOnly = false }) {
  const upiUrl = (paymentDue.upi_pay_url || '').trim();
  const staticPosterUrl = posterUrlWithVpa(paymentDue.static_upi_qr_url, paymentDue.upi_vpa);
  const { paytmCashWallet, paytmMobile, gpay, phonepe } = customerPayUrls(paymentDue);
  const canPaytm = Boolean(paytmCashWallet || paytmMobile);
  const canGPay = Boolean(gpay || upiUrl);
  const canPhonePe = Boolean(phonepe || upiUrl);
  const vpa = (paymentDue.upi_vpa || '').trim();
  const qrSize = scanOnly ? 176 : isEarlyCod ? 176 : 168;
  const scannerLabel = scanOnly
    ? `Scan to pay ₹${amountLabel}`
    : isEarlyCod
      ? 'Tap to pay now with Paytm'
      : 'Tap to pay with Paytm';

  if (!upiUrl && !staticPosterUrl && !canPaytm) {
    return <p className="customer-pay-fallback">Please call us to complete payment.</p>;
  }

  return (
    <>
      {upiUrl ? (
        scanOnly ? (
          <div className="customer-pay-scanner-btn-v2">
            <span className="customer-pay-scanner-label-navy">{scannerLabel}</span>
            <div className="customer-pay-scanner-qr-wrap">
              {staticPosterUrl ? (
                <img
                  src={staticPosterUrl}
                  alt={`Pay ₹${amountLabel} to ${payee} via UPI`}
                  style={{ display: 'block', width: 'min(100%, 360px)', height: 'auto' }}
                />
              ) : (
                <OrderUpiQr
                  upiUrl={upiUrl}
                  size={qrSize}
                  alt={`Pay ₹${amountLabel} to ${payee}`}
                  scanFriendly
                />
              )}
            </div>
            {vpa ? <p className="customer-pay-vpa">UPI <strong>{vpa}</strong></p> : null}
          </div>
        ) : (
          <button
            type="button"
            className="customer-pay-scanner-btn-v2"
            onClick={() => openCustomerPaytm(paymentDue)}
            aria-label={`Tap to pay ₹${amountLabel} with Paytm`}
          >
            <span className="customer-pay-scanner-label-navy">{scannerLabel}</span>
            <div className="customer-pay-scanner-qr-wrap">
              <OrderUpiQr
                upiUrl={upiUrl}
                size={qrSize}
                alt={`Pay ₹${amountLabel} to ${payee}`}
                frame="paytm"
              />
            </div>
          </button>
        )
      ) : null}

      {!scanOnly && (canGPay || canPhonePe) ? (
        <div className="customer-pay-alt-apps customer-pay-alt-apps--branded">
          {canGPay ? (
            <button
              type="button"
              className="customer-pay-gpay-btn"
              onClick={() => openCustomerGPay(paymentDue)}
            >
              <span className="customer-pay-gpay-word">
                <span className="customer-pay-gpay-g">G</span>
                <span className="customer-pay-gpay-o1">o</span>
                <span className="customer-pay-gpay-o2">o</span>
                <span className="customer-pay-gpay-g2">g</span>
                <span className="customer-pay-gpay-l">l</span>
                <span className="customer-pay-gpay-e">e</span>
                <span className="customer-pay-gpay-pay">Pay</span>
              </span>
            </button>
          ) : null}
          {canPhonePe ? (
            <button
              type="button"
              className="customer-pay-phonepe-btn"
              onClick={() => openCustomerPhonePe(paymentDue)}
            >
              PhonePe
            </button>
          ) : null}
        </div>
      ) : null}

      {vpa && !scanOnly ? (
        <p className="customer-pay-vpa">
          Verified on Paytm: <strong>{payee}</strong>
          {' · UPI '}
          <strong>{vpa}</strong>
        </p>
      ) : null}
    </>
  );
}

function PaySectionBody({
  paymentDue,
  amountLabel,
  payee,
  isEarlyCod,
  scanOnly = false,
}) {
  return (
    <>
      <OrderUpiPrimaryBlock
        paymentDue={paymentDue}
        amountLabel={amountLabel}
        payee={payee}
        isEarlyCod={isEarlyCod}
        scanOnly={scanOnly}
      />
    </>
  );
}

/** COD payment on CW / TD. TD scan-only mode shows the approved static Paytm poster. */
export default function CustomerUnpaidPaySection({
  paymentDue,
  embedded = false,
  scanOnly = false,
}) {
  if (!paymentDue?.show) return null;

  const mode = String(paymentDue?.payment_mode || '').trim().toLowerCase();
  const isEarlyCod = mode === 'early_cod';
  const isPayLater =
    mode === 'pay_later' ||
    String(paymentDue?.cod_collection || '').trim().toUpperCase() === 'UNPAID';
  if (!isEarlyCod && !isPayLater) return null;

  const payee = (paymentDue.payee_name || 'The Table Top').trim();
  const amountLabel = formatInr(paymentDue.balance_due);

  if (embedded) {
    return (
      <section className="customer-pay-due customer-pay-due--embedded customer-pay-due--trust" aria-label="Pay now">
        <p className="customer-pay-trust-amount">₹{amountLabel}</p>
        <PaySectionBody
          paymentDue={paymentDue}
          amountLabel={amountLabel}
          payee={payee}
          isEarlyCod={isEarlyCod}
          scanOnly={scanOnly}
        />
      </section>
    );
  }

  return (
    <section
      className={`customer-pay-due customer-pay-due--open customer-pay-due--v2${
        isEarlyCod ? ' customer-pay-due--early-cod' : ' customer-pay-due--pay-later'
      }`}
      aria-label={isEarlyCod ? 'Pay your COD order early' : 'Payment pending'}
    >
      <div className="customer-pay-tab-wrap">
        <div className="customer-pay-tab" role="status">Pay now</div>
      </div>

      {isEarlyCod ? (
        <p className="customer-pay-due-kicker">Cash on delivery · upcoming order</p>
      ) : (
        <p className="customer-pay-due-kicker">Payment pending</p>
      )}

      <p className="customer-pay-due-amount customer-pay-due-amount--v2">₹{amountLabel}</p>

      {!isEarlyCod && paymentDue.is_pay_later ? (
        <p className="customer-pay-due-note customer-pay-due-note--short">No worries — pay when it suits you.</p>
      ) : null}

      <PaySectionBody
        paymentDue={paymentDue}
        amountLabel={amountLabel}
        payee={payee}
        isEarlyCod={isEarlyCod}
        scanOnly={scanOnly}
      />
    </section>
  );
}

export function customerPayAccordionTitle(paymentDue) {
  if (!paymentDue?.show) return '';
  return `Pay ₹${formatInr(paymentDue.balance_due)} now`;
}
